import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect, RMSDatePicker } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchPlaceAutoComplete from '../../../shared/components/modules/SearchPlaceAutoComplete';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import personEmploymentSchema from '../../schema/personEmploymentSchema';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import { DEFAULT_VALUES } from '../../constants/personEmploymentFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonEmployment, editPersonEmployment } from '../../services/userService';

const PersonEmploymentDialog = ({ open, onClose, personId, employment = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(employment?.ptsPlaceID);

  const methods = useForm({
    resolver: CreateFormResolver(personEmploymentSchema),
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(employment || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const {
    positionOptions,
    occupationOptions,
    rankOptions,
    employmentStatusOptions,
    payRateOptions,
    terminationReasonOptions,
  } = useUserDictionaryOptions();
  const ptsPlaceID = useWatch({ control: methods.control, name: 'ptsPlaceID' });
  const ptsSupervisorID = useWatch({ control: methods.control, name: 'ptsSupervisorID' });
  const hired = useWatch({ control: methods.control, name: 'Hired' });
  const terminated = useWatch({ control: methods.control, name: 'Terminated' });

  const handleSupervisorCreated = useCallback(
    (person) => {
      methods.setValue('ptsSupervisorID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const handleEmployerCreated = useCallback(
    (place) => {
      methods.setValue('ptsPlaceID', place?.ptsPlaceID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsPersonID: personId,
        ptsPlaceID: data.ptsPlaceID,
        DepartmentName: data.DepartmentName || null,
        Position: data.Position || null,
        Occupation: data.Occupation || null,
        Rank: data.Rank || null,
        Status: data.Status || null,
        Pay: data.Pay || null,
        Rate: data.Rate || null,
        ptsSupervisorID: data.ptsSupervisorID || null,
        Shift: data.Shift || null,
        Hired: data.Hired || null,
        Terminated: data.Terminated || null,
        TerminationReason: data.TerminationReason || null,
        Notes: data.Notes || null,
      };

      if (isEditMode) {
        payload.UpdatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? editPersonEmployment(
                { ptsPersonID: personId, ptsPlaceID: employment.ptsPlaceID },
                payload
              )
            : createPersonEmployment(payload),
        onSuccess: (savedEmployment) => {
          dispatch(
            notify(
              isEditMode ? 'Employment updated successfully' : 'Employment created successfully',
              'success'
            )
          );
          onSaved?.(savedEmployment);
        },
      });
    },
    [isEditMode, employment, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this employment record. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Employment' : 'Add Employment'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!employment?.Created}
            created={employment?.Created}
            createdBy={employment?.CreatedBy}
            updated={employment?.Updated}
            updatedBy={employment?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchPlaceAutoComplete
              name="ptsPlaceID"
              label="Employer"
              defaultPlaceId={ptsPlaceID}
              onPlaceCreated={handleEmployerCreated}
              onPlaceUpdated={handleEmployerCreated}
              disabled={isEditMode}
              showAddIcon={!isEditMode}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField
              name="DepartmentName"
              label="Department Name"
              inputProps={{ maxLength: 40 }}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Position"
              label="Position"
              options={positionOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Occupation"
              label="Occupation"
              options={occupationOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Rank"
              label="Rank"
              options={rankOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Status"
              label="Status"
              options={employmentStatusOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="Pay" label="Pay" type="number" />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Rate"
              label="Rate"
              options={payRateOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <SearchPersonAutoComplete
              name="ptsSupervisorID"
              label="Supervisor"
              defaultPersonId={ptsSupervisorID}
              onPersonCreated={handleSupervisorCreated}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="Shift" label="Shift" inputProps={{ maxLength: 50 }} />
          </Grid>
          <Grid item xs={6}>
            <RMSDatePicker name="Hired" label="Hired" maxDate={terminated || undefined} />
          </Grid>
          <Grid item xs={6}>
            <RMSDatePicker name="Terminated" label="Terminated" minDate={hired || undefined} />
          </Grid>
          <Grid item xs={12}>
            <RMSSelect
              name="TerminationReason"
              label="Termination Reason"
              options={terminationReasonOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="Notes"
              label="Notes"
              multiline
              rows={3}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonEmploymentDialog;
